import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, doc, getDoc, setDoc, addDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig.js';
import { getAuth, updatePassword, EmailAuthProvider, reauthenticateWithCredential, sendPasswordResetEmail } from 'firebase/auth';
import '../styling/settings.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaUser, FaUsers, FaBuilding, FaCog, FaPlus, FaEdit, FaToggleOn, FaToggleOff } from 'react-icons/fa';

const Settings = ({ setStep, userInfo }) => {

  const [activeTab, setActiveTab] = useState('profile');
  const [selectedPractice, setSelectedPractice] = useState('');
  const [users, setUsers] = useState([]);
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingPracticeId, setEditingPracticeId] = useState(null);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [isEditingPractice, setIsEditingPractice] = useState(false);
  const [isAddingPractice, setIsAddingPractice] = useState(false);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [profileData, setProfileData] = useState({});
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });

  const staticPractices = [
    "FWOS", "Park Cities", "Sonoma County", "Hill & Ioppolo", "Ornstein",
    "Saomsa", "Cosic", "Napa Sonoma", "Facial & Oral Surgery Associates",
    "Petaluma", "Antelope Valley", "Synergy", "Evergreen", "Three Rivers",
    "Alameda", "Newhall", "WestsideOMS", "Oral Surgery Associates West Ten.",
    "Oral Surgery Associates", "Oregon Oral Surgeons", "Elevation Center",
    "Peninsula Center", "Advanced Oral & Maxillofacial Surgery",
    "Integrative Oral & Facial Surgery", "Shroyer", "Memorial", "Paramount",
    "Facial Surgery Institute"
  ];

  const roles = ["Standard", "Practice Administrator", "System Administrator"];
  const statuses = ["Pending", "Active", "Inactive"];

  // Determine available tabs based on user role
  const getAvailableTabs = () => {
    const tabs = [
      { id: 'profile', label: 'My Profile', icon: <FaUser /> }
    ];

    if (userInfo.role === 'Practice Administrator') {
      tabs.push({ id: 'users', label: 'User Management', icon: <FaUsers /> });
      tabs.push({ id: 'practice', label: 'Practice Settings', icon: <FaBuilding /> });
    }

    if (userInfo.role === 'System Administrator') {
      tabs.push({ id: 'users', label: 'User Management', icon: <FaUsers /> });
      tabs.push({ id: 'practices', label: 'Practice Management', icon: <FaBuilding /> });
      tabs.push({ id: 'system', label: 'System Settings', icon: <FaCog /> });
    }

    return tabs;
  };

  const availableTabs = getAvailableTabs();

  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  useEffect(() => {
    // Initialize profile data
    setProfileData({
      firstName: userInfo.firstName || '',
      lastName: userInfo.lastName || '',
      email: userInfo.email || '',
      mobile: formatPhoneNumber(userInfo.mobile || ''),
      practice: userInfo.practice || '',
      role: userInfo.role || ''
    });
  }, [userInfo]);

  const fetchUsers = useCallback(async () => {
    try {
      const usersCollection = collection(db, 'users');
      const userSnapshot = await getDocs(usersCollection);
      const userList = userSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const filteredUsers = userList.filter(user => {
        if (userInfo.role === 'System Administrator') {
          return selectedPractice ? user.practice === selectedPractice : true;
        } else if (userInfo.role === 'Practice Administrator') {
          return user.practice === userInfo.practice;
        } else {
          return false;
        }
      });

      setUsers(filteredUsers);
    } catch (error) {
      console.error("Error fetching users: ", error);
    }
  }, [selectedPractice, userInfo]);

  const fetchPractices = useCallback(async () => {
    try {
      const practicesCollection = collection(db, 'practices');
      const practiceSnapshot = await getDocs(practicesCollection);
      const practiceList = practiceSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const filteredPractices = practiceList.filter(practice => {
        if (userInfo.role === 'System Administrator') {
          return true;
        } else if (userInfo.role === 'Practice Administrator') {
          return practice.practiceName === userInfo.practice;
        } else {
          return false;
        }
      });

      setPractices(filteredPractices);
    } catch (error) {
      console.error("Error fetching practices: ", error);
    }
  }, [userInfo]);

  useEffect(() => {
    fetchUsers();
    fetchPractices();
    setLoading(false);
  }, [fetchUsers, fetchPractices]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (user) {
        const userQuery = await getDocs(collection(db, 'users'));
        const userDoc = userQuery.docs.find(doc => doc.data().email === user.email.toLowerCase());
        
        if (userDoc) {
          await updateDoc(doc(db, 'users', userDoc.id), profileData);
          setMessage({ type: 'success', text: 'Profile updated successfully!' });
          toast.success('Profile updated successfully!');
        }
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error updating profile: ' + error.message });
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match!' });
      return;
    }

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (user) {
        // Re-authenticate user
        const credential = EmailAuthProvider.credential(
          user.email,
          passwordData.currentPassword
        );
        
        await reauthenticateWithCredential(user, credential);
        await updatePassword(user, passwordData.newPassword);
        setMessage({ type: 'success', text: 'Password changed successfully!' });
        toast.success('Password changed successfully!');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error changing password: ' + error.message });
      toast.error('Error changing password');
    }
  };
  
  const handleDisableUser = async (userId) => {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      const currentStatus = userDoc.data().status;
      
      await updateDoc(userRef, {
        status: currentStatus === 'Active' ? 'Inactive' : 'Active'
      });
      
      await fetchUsers();
      setMessage({ type: 'success', text: `User ${currentStatus === 'Active' ? 'deactivated' : 'activated'} successfully!` });
    } catch (error) {
      setMessage({ type: 'error', text: 'Error updating user status: ' + error.message });
    }
  };
  
  const handleEditUser = async (userId) => {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        setFormData(userDoc.data());
        setEditingUserId(userId);
        setIsEditingUser(true);
      }
    } catch (error) {
      console.error("Error fetching user data: ", error);
    }
  };

  const handleAddNewUser = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      mobile: '',
      practice: userInfo.role === 'Practice Administrator' ? userInfo.practice : '',
      role: 'Standard',
      status: 'Pending'
    });
    setIsAddingUser(true);
  };



  const handleSubmitUser = async (e) => {
    e.preventDefault();

    try {
      if (isAddingUser) {
        if ((formData.tempPassword || formData.confirmTempPassword) && formData.tempPassword !== formData.confirmTempPassword) {
          setMessage({ type: 'error', text: 'Temporary passwords do not match.' });
          return;
        }
        // Add new user
        await addDoc(collection(db, 'users'), {
          ...formData,
          createdAt: new Date(),
          createdBy: userInfo.email
        });
        // Send reset email so user can set their own password securely
        try { await sendPasswordResetEmail(getAuth(), formData.email); } catch {}
        setMessage({ type: 'success', text: 'User added successfully!' });
        // Close modal first, then show toast
        setIsEditingUser(false);
        setIsAddingUser(false);
        setTimeout(() => {

          toast.success('User added successfully!', {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          fetchUsers();
        }, 200);
      } else {
        if ((formData.tempPassword || formData.confirmTempPassword) && formData.tempPassword !== formData.confirmTempPassword) {
          setMessage({ type: 'error', text: 'Temporary passwords do not match.' });
          return;
        }
        // Update existing user
        const userRef = doc(db, 'users', editingUserId);
        await updateDoc(userRef, formData);
        // Trigger password reset to update credentials if admin entered a temp password
        if (formData.tempPassword && formData.confirmTempPassword) {
          try { await sendPasswordResetEmail(getAuth(), formData.email); } catch {}
        }
        setMessage({ type: 'success', text: 'User updated successfully!' });
        // Close modal first, then show toast
        setIsEditingUser(false);
        setIsAddingUser(false);
        setTimeout(() => {

          toast.success('User updated successfully!', {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          fetchUsers();
        }, 200);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving user: ' + error.message });
      setTimeout(() => toast.error('Error saving user'), 100);
    }
  };

  const handleEditPractice = async (practiceId) => {
    try {
      const practiceRef = doc(db, 'practices', practiceId);
      const practiceDoc = await getDoc(practiceRef);

      if (practiceDoc.exists()) {
        setFormData(practiceDoc.data());
        setEditingPracticeId(practiceId);
        setIsEditingPractice(true);
      }
    } catch (error) {
      console.error("Error fetching practice data: ", error);
    }
  };

  const handleDisablePractice = async (practiceId) => {
    try {
      const practiceRef = doc(db, 'practices', practiceId);
      const practiceDoc = await getDoc(practiceRef);
      const currentStatus = practiceDoc.data().status;
      
      await updateDoc(practiceRef, {
        status: currentStatus === 'Active' ? 'Inactive' : 'Active'
      });
      
      await fetchPractices();
      setMessage({ type: 'success', text: `Practice ${currentStatus === 'Active' ? 'deactivated' : 'activated'} successfully!` });
    } catch (error) {
      setMessage({ type: 'error', text: 'Error updating practice status: ' + error.message });
    }
  };

  const handleSubmitPractice = async (e) => {
    e.preventDefault();
    try {
      if (isAddingPractice) {
        await addDoc(collection(db, 'practices'), {
          practiceName: formData.practiceName,
          officeAddress: formData.officeAddress,
          officeCity: formData.officeCity,
          officeState: formData.officeState,
          officeZipCode: formData.officeZipCode,
          officePhone: formData.officePhone,
          email: formData.email,
          status: formData.status || 'Active',
        });
        setMessage({ type: 'success', text: 'Practice created successfully!' });
        // Close modal first, then show toast
        setIsAddingPractice(false);
        setIsEditingPractice(false);
        setTimeout(() => {
          toast.success('Practice created successfully!');
          fetchPractices();
        }, 200);
      } else {
        const practiceRef = doc(db, 'practices', editingPracticeId);
        await updateDoc(practiceRef, formData);
        setMessage({ type: 'success', text: 'Practice updated successfully!' });
        // Close modal first, then show toast
        setIsEditingPractice(false);
        setTimeout(() => {
          toast.success('Practice updated successfully!');
          fetchPractices();
        }, 200);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error updating practice: ' + error.message });
      setTimeout(() => toast.error('Error updating practice'), 100);
    }
  };

  const handleAddPractice = () => {
    setFormData({
      practiceName: '',
      officeAddress: '',
      officeCity: '',
      officeState: '',
      officeZipCode: '',
      officePhone: '',
      email: '',
      status: 'Active'
    });
    setEditingPracticeId(null);
    setIsAddingPractice(true);
    setIsEditingPractice(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;
    
    // Format phone numbers
    if (name === 'mobile' || name === 'officePhone') {
      formattedValue = formatPhoneNumber(value);
    }
    
    if (activeTab === 'profile') {
      setProfileData({ ...profileData, [name]: formattedValue });
    } else {
      // Clear practice field when System Administrator role is selected
      if (name === 'role' && value === 'System Administrator') {
        setFormData({ ...formData, [name]: formattedValue, practice: '' });
      } else {
        setFormData({ ...formData, [name]: formattedValue });
      }
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });
  };

  if (loading) {
    return <div className="loading-container">Loading...</div>;
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>Settings</h1>
        <p className="settings-subtitle">Manage your account and system preferences</p>
      </div>



      <div className="settings-tabs">
        {availableTabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="settings-content">
        {activeTab === 'profile' && (
          <div className="profile-section">
            <div className="section-card">
              <h2>Personal Information</h2>
              <form onSubmit={handleUpdateProfile} className="profile-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name</label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      value={profileData.firstName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name</label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      value={profileData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={profileData.email}
                      onChange={handleChange}
                      disabled
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="mobile">Mobile</label>
                    <input
                      id="mobile"
                      name="mobile"
                      type="tel"
                      value={profileData.mobile}
                      onChange={handleChange}
                      placeholder="(XXX) XXX-XXXX"
                      maxLength="14"
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="practice">Practice</label>
                    <input
                      id="practice"
                      name="practice"
                      type="text"
                      value={profileData.practice}
                      disabled
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="role">Role</label>
                    <input
                      id="role"
                      name="role"
                      type="text"
                      value={profileData.role}
                      disabled
                    />
                  </div>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn-primary">Update Profile</button>
                </div>
              </form>
            </div>

            <div className="section-card">
              <h2>Change Password</h2>
              <form onSubmit={handleChangePassword} className="password-form">
                <div className="form-group">
                  <label htmlFor="currentPassword">Current Password</label>
                  <input
                    id="currentPassword"
                    name="currentPassword"
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn-primary">Change Password</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="users-section">
            <div className="section-card">
              <div className="section-header-row">
                <h2>User Management</h2>
                <button onClick={handleAddNewUser} className="btn-add">
                  <FaPlus /> Add User
                </button>
              </div>

              {userInfo.role === 'System Administrator' && (
                <div className="filter-row">
                  <label>
                    Filter by Practice:
                    <select
                      value={selectedPractice}
                      onChange={(e) => setSelectedPractice(e.target.value)}
                      className="practice-filter"
                    >
                      <option value="">All Practices</option>
                      {staticPractices.map((practice, index) => (
                        <option key={index} value={practice}>{practice}</option>
                      ))}
                    </select>
                  </label>
                </div>
              )}

              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, index) => (
                      <tr key={user.id}>
                        <td>{index + 1}</td>
                        <td>{user.firstName} {user.lastName}</td>
                        <td>{user.email}</td>
                        <td>{formatPhoneNumber(user.mobile || '')}</td>
                        <td>
                          <span className="role-badge">{user.role}</span>
                        </td>
                        <td>
                          <span className={`status-badge ${user.status?.toLowerCase()}`}>
                            {user.status}
                          </span>
                        </td>
                        <td>
                          <div className="table-actions">
                            <button 
                              onClick={() => handleEditUser(user.id)}
                              className="btn-icon edit"
                              title="Edit"
                            >
                              <FaEdit />
                            </button>
                            <button 
                              onClick={() => handleDisableUser(user.id)}
                              className={`btn-icon ${user.status === 'Active' ? 'disable' : 'enable'}`}
                              title={user.status === 'Active' ? 'Deactivate' : 'Activate'}
                            >
                              {user.status === 'Active' ? <FaToggleOff /> : <FaToggleOn />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {(activeTab === 'practice' || activeTab === 'practices') && (
          <div className="practices-section">
            <div className="section-card">
              <div className="section-header-row">
                <h2>Practice Management</h2>
                {(userInfo.role === 'System Administrator' || (userInfo.role === 'Practice Administrator' && practices.length === 0)) && (
                  <button onClick={handleAddPractice} className="btn-add">
                    <FaPlus /> Add Practice
                  </button>
                )}
              </div>

              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Location</th>
                      <th>Contact</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {practices
                      .filter(practice => 
                        selectedPractice ? practice.practiceName === selectedPractice : true
                      )
                      .map((practice, index) => (
                        <tr key={practice.id}>
                          <td>{index + 1}</td>
                          <td>{practice.practiceName}</td>
                          <td>{practice.email}</td>
                          <td>
                            <div className="location-cell">
                              {practice.officeCity && practice.officeState && 
                                `${practice.officeCity}, ${practice.officeState}`}
                            </div>
                          </td>
                          <td>{formatPhoneNumber(practice.officePhone || '')}</td>
                          <td>
                            <span className={`status-badge ${practice.status?.toLowerCase()}`}>
                              {practice.status}
                            </span>
                          </td>
                          <td>
                            <div className="table-actions">
                              <button 
                                className="btn-icon edit"
                                onClick={() => handleEditPractice(practice.id)}
                                title="Edit"
                              >
                                <FaEdit />
                              </button>
                              {userInfo.role === 'System Administrator' && (
                                <button 
                                  className={`btn-icon ${practice.status === 'Active' ? 'disable' : 'enable'}`}
                                  onClick={() => handleDisablePractice(practice.id)}
                                  title={practice.status === 'Active' ? 'Deactivate' : 'Activate'}
                                >
                                  {practice.status === 'Active' ? <FaToggleOff /> : <FaToggleOn />}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    {false}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'system' && (
          <div className="system-section">
            <div className="section-card">
              <h2>System Configuration</h2>
              <div className="system-info">
                <div className="info-row">
                  <span className="info-label">Total Users:</span>
                  <span className="info-value">{users.length}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Total Practices:</span>
                  <span className="info-value">{practices.length}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Active Users:</span>
                  <span className="info-value">
                    {users.filter(u => u.status === 'Active').length}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">System Version:</span>
                  <span className="info-value">1.0.0</span>
                </div>
              </div>
            </div>

            <div className="section-card">
              <h2>Audit Log</h2>
              <p className="placeholder-text">Audit logging coming soon...</p>
            </div>
          </div>
        )}
      </div>

      {/* User Edit/Add Modal */}
      {(isEditingUser || isAddingUser) && (
        <div className="settings-modal-overlay">
          <div className="settings-modal">
            <div className="modal-header">
              <h3>{isAddingUser ? 'Add New User' : 'Edit User'}</h3>
              <button 
                className="modal-close"
                onClick={() => {
                  setIsEditingUser(false);
                  setIsAddingUser(false);
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmitUser} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input 
                    id="firstName"
                    name="firstName" 
                    value={formData.firstName || ''} 
                    onChange={handleChange} 
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input 
                    id="lastName"
                    name="lastName" 
                    value={formData.lastName || ''} 
                    onChange={handleChange} 
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input 
                    id="email"
                    name="email" 
                    type="email"
                    value={formData.email || ''} 
                    onChange={handleChange} 
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="mobile">Mobile</label>
                  <input 
                    id="mobile"
                    name="mobile" 
                    type="tel"
                    value={formData.mobile || ''} 
                    onChange={handleChange}
                    placeholder="(XXX) XXX-XXXX"
                    maxLength="14"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                {formData.role !== 'System Administrator' && (
                  <div className="form-group">
                    <label htmlFor="practice">Practice</label>
                    <select
                      id="practice"
                      name="practice"
                      value={formData.practice || ''}
                      onChange={handleChange}
                      required
                      disabled={userInfo.role === 'Practice Administrator'}
                    >
                      <option value="">Select Practice</option>
                      {staticPractices.map((practice, index) => (
                        <option key={index} value={practice}>{practice}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="role">Role</label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role || ''}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Role</option>
                    {roles.map((role, index) => {
                      // Practice Admins can't create System Admins
                      if (userInfo.role === 'Practice Administrator' && role === 'System Administrator') {
                        return null;
                      }
                      return <option key={index} value={role}>{role}</option>;
                    })}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status || ''}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Status</option>
                  {statuses.map((status, index) => (
                    <option key={index} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              {/* Temporary password for Add/Edit user */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="tempPassword">Temporary Password</label>
                  <input
                    id="tempPassword"
                    name="tempPassword"
                    type="password"
                    value={formData.tempPassword || ''}
                    onChange={handleChange}
                    placeholder="Optional"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="confirmTempPassword">Confirm Temp Password</label>
                  <input
                    id="confirmTempPassword"
                    name="confirmTempPassword"
                    type="password"
                    value={formData.confirmTempPassword || ''}
                    onChange={handleChange}
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div className="modal-actions">

                <button 
                  type="button"
                  className="btn-primary"
                  onClick={() => handleSubmitUser({ preventDefault: () => {} })}
                >
                  {isAddingUser ? 'Add User' : 'Save Changes'}
                </button>
                <button 
                  type="button"
                  onClick={() => {
                    setIsEditingUser(false);
                    setIsAddingUser(false);
                  }}
                  className="btn-primary btn-ghost"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Practice Edit Modal */}
      {isEditingPractice && (
        <div className="settings-modal-overlay">
          <div className="settings-modal">
            <div className="modal-header">
              <h3>{isAddingPractice ? 'Add Practice' : 'Edit Practice'}</h3>
              <button 
                className="modal-close"
                onClick={() => setIsEditingPractice(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmitPractice} className="modal-form">
              <div className="form-group">
                <label htmlFor="practiceName">Practice Name</label>
                <select 
                  id="practiceName"
                  name="practiceName" 
                  value={formData.practiceName || ''} 
                  onChange={handleChange} 
                  required
                >
                  <option value="">Select Practice</option>
                  {staticPractices.map((practice, index) => (
                    <option key={index} value={practice}>{practice}</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="officeAddress">Office Address</label>
                  <input 
                    id="officeAddress"
                    name="officeAddress" 
                    value={formData.officeAddress || ''} 
                    onChange={handleChange} 
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="officeCity">City</label>
                  <input 
                    id="officeCity"
                    name="officeCity" 
                    value={formData.officeCity || ''} 
                    onChange={handleChange} 
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="officeState">State</label>
                  <input 
                    id="officeState"
                    name="officeState" 
                    value={formData.officeState || ''} 
                    onChange={handleChange} 
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="officeZipCode">Zip Code</label>
                  <input 
                    id="officeZipCode"
                    name="officeZipCode" 
                    value={formData.officeZipCode || ''} 
                    onChange={handleChange} 
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="officePhone">Phone</label>
                  <input 
                    id="officePhone"
                    name="officePhone" 
                    type="tel"
                    value={formData.officePhone || ''} 
                    onChange={handleChange}
                    placeholder="(XXX) XXX-XXXX"
                    maxLength="14"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input 
                    id="email"
                    name="email" 
                    type="email"
                    value={formData.email || ''} 
                    onChange={handleChange} 
                    required
                  />
                </div>
              </div>

              {/* No admin password creation when adding practice */}

              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status || ''}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Status</option>
                  {statuses.map((status, index) => (
                    <option key={index} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn-primary">Save Changes</button>
                <button 
                  type="button" 
                  onClick={() => setIsEditingPractice(false)} 
                  className="btn-primary btn-ghost"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
